import { CTA, trackAttrs } from '../../../utils/track'
import { btnSolid, btnOutline } from '../../../utils/buttonAccent'
import { MAVEN_PROFILE_URL, OFFER_LABEL, formatSessionDate, formatDuration, type Offer } from '../../../config/cta'

/**
 * Everything on Maven that a reader can still act on, straight from the admin
 * table: the paid cohorts first, then the free sessions as dated rows.
 *
 * A cohort with a start date is open for enrolment, so it gets the solid
 * button. One without a date is a waitlist, which is still worth a click but
 * not the same promise, so it gets the outline button and says "waitlist".
 */
export default function MavenOffers({ offers, placement }: { offers: Offer[]; placement: string }) {
  const paid = offers.filter((o) => o.kind !== 'lesson')
  const lessons = offers.filter((o) => o.kind === 'lesson' && o.startsAt)
  if (!paid.length && !lessons.length) return null

  return (
    <div className="flex flex-col gap-10">
      {paid.length ? (
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {paid.map((o) => {
            const open = Boolean(o.startsAt)
            return (
              <article key={o.id} className={`flex flex-col rounded-xl border bg-white p-6 ${open ? 'border-brand-gold' : 'border-border-subtle'}`}>
                <span className="font-heading text-overline uppercase tracking-[0.14em] text-brand-red mb-3">
                  {open ? 'Enrolling now · ' : 'Waitlist · '}{OFFER_LABEL[o.kind]}
                </span>
                <h3 className="font-heading text-h4 font-bold text-brand-navy leading-snug">{o.title}</h3>
                {o.blurb ? <p className="font-body text-body-sm text-ink-soft mt-3 leading-relaxed">{o.blurb}</p> : null}
                {open && formatSessionDate(o.startsAt) ? (
                  <p className="m-0 mt-4 font-heading text-caption font-semibold text-brand-navy">Starts {formatSessionDate(o.startsAt)}</p>
                ) : null}
                {o.promo ? (
                  <p className="mt-4 rounded-smooth border border-brand-gold/50 bg-brand-gold/10 px-3 py-2 font-heading text-caption font-semibold text-brand-navy">
                    {o.promo}
                  </p>
                ) : null}
                <div className="mt-auto pt-6">
                  <a
                    href={o.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    {...trackAttrs(CTA.maven, placement, { target: o.id })}
                    className={open ? btnSolid('gold') : btnOutline('navy')}
                  >
                    {open ? 'Enroll on Maven ↗' : 'Join the waitlist ↗'}
                  </a>
                </div>
              </article>
            )
          })}
        </div>
      ) : null}

      {lessons.length ? (
        <div>
          <h3 className="font-heading text-overline uppercase tracking-[0.14em] text-ink-muted mb-4">Free live sessions · sign up on Maven</h3>
          <ul className="m-0 flex list-none flex-col gap-3 p-0">
            {lessons.map((o) => (
              <li key={o.id} className="flex flex-col gap-4 rounded-xl border border-border-subtle bg-white p-5 sm:flex-row sm:items-center">
                <div className="flex-1">
                  <p className="m-0 font-heading text-caption font-semibold text-brand-navy/60">
                    {formatSessionDate(o.startsAt)}
                    {formatDuration(o.durationMins) ? ` · ${formatDuration(o.durationMins)} · Free` : ' · Free'}
                  </p>
                  <h4 className="m-0 mt-1 font-heading text-body font-bold text-brand-navy leading-snug">{o.title}</h4>
                </div>
                <a
                  href={o.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  {...trackAttrs(CTA.lesson, placement, { target: o.id })}
                  className={`${btnSolid('gold')} shrink-0`}
                >
                  Save my free seat ↗
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <a
        href={MAVEN_PROFILE_URL}
        target="_blank"
        rel="noopener noreferrer"
        {...trackAttrs(CTA.maven, placement, { target: 'maven-profile' })}
        className="font-heading text-body-sm font-semibold text-brand-navy underline decoration-brand-navy/40 hover:decoration-brand-navy/80 w-fit"
      >
        See everything I teach on Maven ↗
      </a>
    </div>
  )
}
